import {
  istAktuelleGeneration,
  rankCatalogCandidates,
  unitsIncompatibleMitPriceUnitFallback,
  type PositionForCatalogMatch,
  type RankedCatalogCandidate,
} from './catalog-position-match';
import { effectiveUnitPrice } from './effective-unit-price';
import {
  BLITZ_ZEILEN_IDS,
  effektiverMaterialAufschlag,
  hatKalkulation,
  materialEkImportBlockiertDurchBestandskalkulation,
  mengenBezugFuerPreisImport,
  nichtUebernehmbarWegenNullOderNegativemPreis,
  rundetStillAufNull,
  setzeBlitzZeile,
  type KalkulationsDefaults,
} from './kalkulation';
import type { PositionsKalkulation } from './schema';

/**
 * Batch-Modus für den Katalog-Vorschlag: "Suche zu ALLEN meinen LV-Positionen automatisch die
 * passenden Großhandelsartikel und schlage sie vor." Reine Auswahl-/Gating-Logik — die UI
 * (client/src/components/kalkulation/catalog-batch-suggest-dialog.tsx) holt nur die Rohdaten
 * (Suche je Position) und delegiert Ranking/Gating/Bulk-Übernahme-Plan komplett hierher, damit
 * die Kernlogik unter `node --test` prüfbar ist (eine .tsx läuft dort nicht).
 *
 * Wiederverwendet AUSSCHLIESSLICH bestehende Bausteine:
 * - rankCatalogCandidates (shared/catalog-position-match.ts) für das Ranking je Position,
 * - effectiveUnitPrice (shared/effective-unit-price.ts) für den EK-Divisor (N-Einheiten-Preis),
 * - materialEkImportBlockiertDurchBestandskalkulation / rundetStillAufNull / setzeBlitzZeile /
 *   mengenBezugFuerPreisImport (shared/kalkulation.ts) — derselbe Guard- und Builder-Pfad wie
 *   client/src/components/kalkulation/catalog-suggest-dialog.tsx (Einzel-Dialog) und
 *   client/src/components/gaeb/X84PriceImport.tsx (X84-Preisrückimport).
 *
 * Oberste Leitlinie (kein stiller Fehlpreis): NUR zone='high' wird vorausgewählt (defaultChecked);
 * medium/low erfordern eine bewusste Nutzerauswahl. Eine blockierte Position ist NIE anhakbar
 * bzw. übernehmbar — buildBatchApplyPlan() filtert `outcome !== 'uebernehmbar'` IMMER heraus,
 * unabhängig vom übergebenen checked-Set (Verteidigung gegen einen hypothetischen UI-Bug, der
 * eine blockierte Zeile fälschlich als checked führt).
 */

export type BatchRowOutcome = 'uebernehmbar' | 'blockiert' | 'kein-treffer' | 'suche-fehlgeschlagen';

/**
 * Katalogartikel wie von GET /api/softec-db/materials/search geliefert (inkl. `priceUnit`, das
 * für effectiveUnitPrice gebraucht wird — siehe shared/effective-unit-price.ts). Lokal definiert
 * statt aus server/routes/softec-db.ts importiert (Server-Code soll nicht ins Client-Bundle
 * gezogen werden) — gleiches Muster wie die lokalen `CatalogSearchResult`-Typen in den
 * Einzel-Dialogen, hier aber EINMAL für die geteilte, pure Batch-Logik.
 */
export interface CatalogSearchCandidate {
  id: string;
  articleNumber: string;
  ean: string | null;
  description: string;
  descriptionLong: string | null;
  unit: string | null;
  price: number | null;
  priceUnit: string | null;
  // Preisfrische + Live-Preis-Naht (2026-08, ADDITIV + optional — ältere Antworten/
  // Fixtures ohne die Felder bleiben gültig): Basis fürs frisch/älter/abgelaufen-Badge
  // (shared/preis-frische.ts) und die Lieferanten-Zuordnung der Live-Preise.
  supplierId?: string;
  priceValidFrom?: string | null;
  priceValidUntil?: string | null;
  updatedAt?: string | null;
}

export interface BatchPositionSource {
  positionId: number;
  position: PositionForCatalogMatch;
  kalkulationBestehend: PositionsKalkulation | null | undefined;
  candidates: CatalogSearchCandidate[];
  /**
   * P2-G: true, wenn die Katalog-Suche für DIESE Position fehlgeschlagen ist (Netzwerkfehler,
   * abgelaufene Session, 5xx-Serverfehler o.ä.) — NICHT einfach `candidates: []`. Ein echter
   * "kein Treffer" (erfolgreiche Suche, aber leeres Ergebnis) darf von einem unsichtbaren
   * Ausfall nicht ununterscheidbar sein. Siehe classifyMaterialsSearchResponse() unten.
   */
  searchFailed?: boolean;
  /**
   * P1-2: true, wenn der Server für DIESE Position mehr Katalogtreffer hatte, als
   * zurückgegeben wurden (SQL-Limit griff, siehe server/routes/softec-db.ts::
   * applySearchResultLimit). Das Ranking (rankCatalogCandidates) lief dann nur auf einem
   * willkürlich abgeschnittenen Kandidaten-Set — der eigentlich beste Treffer könnte
   * außerhalb des Limits liegen. Muss die automatische Vorauswahl für diese Position
   * verhindern (siehe buildBatchCatalogRow), unabhängig davon, wie eindeutig der
   * gefundene Top-Kandidat unter den ZURÜCKGELIEFERTEN aussieht.
   */
  truncated?: boolean;
}

export interface BatchCatalogRow {
  positionId: number;
  kalkulationBestehend: PositionsKalkulation | null | undefined;
  /** Top-N Kandidaten (Ranking), für optionale Alternativen-Anzeige in der Vorschau-Tabelle. */
  ranked: RankedCatalogCandidate[];
  /** Bester Kandidat (ranked[0]) oder null, wenn keiner übrig blieb. */
  best: RankedCatalogCandidate | null;
  /** Effektiver EK (nach Divisor) des besten Kandidaten, oder null ohne nutzbaren Preis. */
  effektiverEk: number | null;
  outcome: BatchRowOutcome;
  blockReason: string | null;
  /**
   * P1-3: true, wenn effectiveUnitPrice() für den besten Kandidaten `unitsIncompatible`
   * gemeldet hat (z.B. quantityUnit "m" vs. priceUnit-Text "100 ST") — dieselbe weiche
   * Warnung, die der Einzeldialog als "Einheiten evtl. inkompatibel" anzeigt. Sichtbar für
   * die UI, damit der Konflikt bei der Bulk-Vorschau nicht stillschweigend untergeht.
   */
  unitsIncompatible: boolean;
  /** P1-2: true, wenn die Katalog-Suche für diese Position abgeschnitten war (mehr Treffer
   * vorhanden, als geliefert) — Ranking lief auf einem unvollständigen Set, siehe
   * BatchPositionSource.truncated. */
  truncated: boolean;
  /** true NUR bei outcome='uebernehmbar' UND zone='high' UND !unitsIncompatible UND !truncated — Kern des Konfidenz-Gatings. */
  defaultChecked: boolean;
}

const SUB_CENT_BLOCK_REASON = 'Preis < 0,01 € je Einheit — für die Kalkulation zu klein.';
const ZERO_OR_NEGATIVE_PRICE_BLOCK_REASON = 'EK 0 € oder negativ — kein gültiger Katalogpreis.';
const BESTANDSKALKULATION_BLOCK_REASON =
  'Bereits eine Blitzkalkulation vorhanden — Material-EK hier nicht automatisch übernehmbar.';
const SEARCH_FAILED_REASON = 'Suche fehlgeschlagen (Netzwerk-/Serverfehler) — bitte erneut versuchen.';

/**
 * P1-C: Mindestabstand (in Konfidenz-Punkten, 0..1) zwischen dem besten und dem
 * zweitbesten Kandidaten, den ein reiner Fuzzy-Treffer haben muss, um automatisch
 * vorausgewählt zu werden. Bewusst konservativ (5 Prozentpunkte) — bei einem
 * Gleichstand oder einem sehr knappen Abstand ist nicht sicher, dass der Top-Kandidat
 * wirklich der richtige ist (z.B. zwei Katalogartikel mit identischer normalisierter
 * Kurzbeschreibung, aber unterschiedlichem Preis). Kein stiller Fehlpreis: im Zweifel
 * NICHT automatisch vorausgewählt, der Nutzer muss dann selbst wählen.
 */
const AMBIGUITY_MARGIN = 0.05;

/**
 * Eine Position ist NICHT batch-fähig, wenn sie eine Titel-Zeile ist (falls das Datenmodell sie
 * als eigene "Position" mit `type: 'title'` führt — defensiver Cast wie im X84PriceImport-Pendant,
 * `positions.filter((p) => (p as any).type !== 'title')`) oder wenn ihr Menge/Text fehlt
 * (Zwischensummen, leere Header-Zeilen).
 */
export function isBatchEligiblePosition(position: {
  type?: string | null;
  name?: string | null;
  beschreibung?: string | null;
  menge?: number | string | null;
}): boolean {
  if ((position.type ?? null) === 'title') return false;
  const hasText = Boolean(
    (position.name ?? '').toString().trim() || (position.beschreibung ?? '').toString().trim(),
  );
  if (!hasText) return false;
  const menge = typeof position.menge === 'number' ? position.menge : Number(position.menge);
  return Number.isFinite(menge) && menge > 0;
}

/**
 * P2-6 (Fix, Codex-Review gpt-5.6, neue Runde): Obergrenze für den automatisch aus einer
 * Position gebauten Katalog-Suchbegriff. Ein GAEB/PDF-Import kann `name` oder `beschreibung`
 * mit einer vollen Langbeschreibung befüllen (mehrere hundert/tausend Zeichen) — ungekappt
 * landet das komplett kodiert in einer GET-URL
 * (`catalog-batch-suggest-dialog.tsx::searchMaterialsForPosition`) und kann das Proxy-/
 * Node-Request-Target-Limit sprengen, BEVOR die Route überhaupt läuft (der Batch meldet dann
 * "Suche fehlgeschlagen" statt eines Treffers). 120 Zeichen sind für einen Freitext-Suchbegriff
 * ohnehin mehr als genug (der Ranker fuzzy-matcht über Tokens, kein zusammenhängender langer
 * Text nötig).
 */
const MAX_BATCH_SEARCH_QUERY_LENGTH = 120;

/**
 * P2-6 (Fix, Codex-Review gpt-5.6, neue Runde): baut den automatischen Katalog-Suchbegriff für
 * EINE Position — bevorzugt `name`, sonst `beschreibung`; die ausgewählte Quelle wird auf
 * {@link MAX_BATCH_SEARCH_QUERY_LENGTH} UTF-16-Codeunits GEKAPPT. Die Kappung trennt kein
 * Surrogatpaar und schneidet am letzten VOLLSTÄNDIGEN Wort innerhalb der Grenze (kein abgeschnittenes
 * Wortfragment als Such-Token, das sonst fälschlich gar keinen Treffer mehr liefern könnte) —
 * nur ein einziges, extrem langes "Wort" ohne jedes Leerzeichen wird hart gekappt. Reine
 * Term-Bau-Funktion, damit sie ohne echten Netzwerk-Request unter node:test geprüft werden kann.
 */
export function buildBatchSearchQuery(
  name: string | null | undefined,
  beschreibung: string | null | undefined,
  maxLength: number = MAX_BATCH_SEARCH_QUERY_LENGTH,
): string {
  const trimmedName = (name ?? '').trim();
  const trimmedBeschreibung = (beschreibung ?? '').trim();
  const selectedSource = trimmedName.length > 0 ? trimmedName : trimmedBeschreibung;
  if (selectedSource.length <= maxLength) return selectedSource;

  let gekappt = selectedSource.slice(0, maxLength);
  const letzteCodeunit = gekappt.charCodeAt(gekappt.length - 1);
  const naechsteCodeunit = selectedSource.charCodeAt(gekappt.length);
  if (
    letzteCodeunit >= 0xd800 &&
    letzteCodeunit <= 0xdbff &&
    naechsteCodeunit >= 0xdc00 &&
    naechsteCodeunit <= 0xdfff
  ) {
    gekappt = gekappt.slice(0, -1);
  }
  const letzteLuecke = gekappt.lastIndexOf(' ');
  const ohneWortfragment = letzteLuecke > 0 ? gekappt.slice(0, letzteLuecke) : gekappt;
  return ohneWortfragment.trim();
}

/** Rankt + gated GENAU EINE Position — reine Funktion, kein Netzwerk-/Store-Zugriff. */
export function buildBatchCatalogRow(source: BatchPositionSource, topN = 5): BatchCatalogRow {
  const truncated = source.truncated === true;
  // P2-G: eine fehlgeschlagene Suche ist NIE "kein Treffer" — unabhängig davon, was in
  // source.candidates steht (sollte ohnehin leer sein), gilt dieser Fall zuerst und gewinnt.
  if (source.searchFailed) {
    return {
      positionId: source.positionId,
      kalkulationBestehend: source.kalkulationBestehend,
      ranked: [],
      best: null,
      effektiverEk: null,
      outcome: 'suche-fehlgeschlagen',
      blockReason: SEARCH_FAILED_REASON,
      unitsIncompatible: false,
      truncated,
      defaultChecked: false,
    };
  }

  const ranked = rankCatalogCandidates(source.position, source.candidates, topN);
  const best = ranked[0] ?? null;

  if (!best) {
    return {
      positionId: source.positionId,
      kalkulationBestehend: source.kalkulationBestehend,
      ranked,
      best: null,
      effektiverEk: null,
      outcome: 'kein-treffer',
      blockReason: null,
      unitsIncompatible: false,
      truncated,
      defaultChecked: false,
    };
  }

  const rawBest = best.candidate as CatalogSearchCandidate;
  const eff = effectiveUnitPrice({ price: rawBest.price, priceUnit: rawBest.priceUnit, quantityUnit: rawBest.unit });
  // P1-3 (Fix, Codex-Fund): effectiveUnitPrice() vergleicht nur candidate-interne Metadaten
  // (das aus price_unit geparste unitLabel gegen quantityUnit) — fehlt quantityUnit (häufig bei
  // Bestandsdaten), bleibt dieser Vergleich wirkungslos ("unbekannt ≠ Veto"), SELBST wenn
  // price_unit eindeutig eine andere Einheitenklasse verrät (z.B. "100 ST" = Stück) als die
  // LV-Position erwartet (z.B. "m"). Fix: fehlt die Kandidaten-eigene Einheit, die aus
  // price_unit geparste eff.unitLabel als FALLBACK-Kandidateneinheit gegen die LV-Einheit
  // (source.position.einheit) prüfen — genau der im Fund genannte fehlende Vergleich.
  // (P2-3: dieselbe Fallback-Logik wiederverwendet der Einzeldialog, siehe
  // shared/catalog-position-match.ts::unitsIncompatibleMitPriceUnitFallback.)
  const unitsIncompatible = unitsIncompatibleMitPriceUnitFallback(eff, rawBest.unit, source.position.einheit);

  if (eff.value === null) {
    // Kein nutzbarer Preis am besten Kandidaten ⇒ wie "kein Treffer" behandeln (nicht
    // "blockiert" — es gibt nichts Anwendbares, das blockiert werden müsste).
    return {
      positionId: source.positionId,
      kalkulationBestehend: source.kalkulationBestehend,
      ranked,
      best,
      effektiverEk: null,
      outcome: 'kein-treffer',
      blockReason: null,
      unitsIncompatible,
      truncated,
      defaultChecked: false,
    };
  }

  let blockReason: string | null = null;
  if (nichtUebernehmbarWegenNullOderNegativemPreis(eff.value)) {
    // P2-F: Batch ist IMMER material-ek — ein EK von 0/negativ würde von setzeBlitzZeile
    // als "leer" verworfen (Blitzzeile bliebe unverändert), ohne dass das sichtbar wird.
    blockReason = ZERO_OR_NEGATIVE_PRICE_BLOCK_REASON;
  } else if (rundetStillAufNull(eff.value)) {
    blockReason = SUB_CENT_BLOCK_REASON;
  } else if (materialEkImportBlockiertDurchBestandskalkulation(source.kalkulationBestehend)) {
    blockReason = BESTANDSKALKULATION_BLOCK_REASON;
  }

  const outcome: BatchRowOutcome = blockReason !== null ? 'blockiert' : 'uebernehmbar';

  // P1-C: NUR vorausgewählt bei (a) einem harten Identifier-Treffer (Artikelnummer/EAN,
  // deterministisch eindeutig) ODER (b) einem eindeutigen Top-Fuzzy-Treffer mit
  // ausreichendem Abstand zum zweiten Kandidaten. Ohne diese Marge würden zwei
  // Kandidaten mit identischer normalisierter Beschreibung (beide confidence 1) den
  // ERSTEN (per Sortierung zufällig obenauf liegenden) vorauswählen — ein stiller,
  // beliebiger Preis.
  const isHardIdentifierMatch = best.method === 'articleNumber' || best.method === 'ean';
  const second = ranked[1] ?? null;
  const hasSufficientMargin =
    best.zone === 'high' && (!second || best.confidence - second.confidence >= AMBIGUITY_MARGIN);
  // P1-3: unitsIncompatible (weich, kein Hard-Fail — siehe effectiveUnitPrice) — im Batch-Modus
  // gibt es aber KEINEN Nutzer, der den Konflikt vor dem Anhaken sieht (anders als im
  // Einzeldialog, wo die Warnung direkt neben dem Kandidaten steht). Deshalb hier hart: eine
  // einheiten-inkompatible Zeile wird NIE automatisch vorausgewählt, selbst bei einem ansonsten
  // harten Identifier-Treffer — der Nutzer muss den Kandidaten bewusst prüfen und manuell anhaken.
  // P1-2: dasselbe gilt für eine TRUNKIERTE Suche — das Ranking lief auf einem willkürlich
  // abgeschnittenen Set, der eigentlich beste Treffer könnte fehlen.
  const defaultChecked =
    outcome === 'uebernehmbar' && !unitsIncompatible && !truncated && (isHardIdentifierMatch || hasSufficientMargin);

  return {
    positionId: source.positionId,
    kalkulationBestehend: source.kalkulationBestehend,
    ranked,
    best,
    effektiverEk: eff.value,
    outcome,
    blockReason,
    unitsIncompatible,
    truncated,
    defaultChecked,
  };
}

export function buildBatchCatalogRows(sources: BatchPositionSource[], topN = 5): BatchCatalogRow[] {
  return sources.map((source) => buildBatchCatalogRow(source, topN));
}

export interface BatchOutcomeCounts {
  uebernehmbar: number;
  blockiert: number;
  keinTreffer: number;
  /** P2-G: Positionen, deren Katalog-Suche fehlgeschlagen ist (NICHT "kein Treffer"). */
  sucheFehlgeschlagen: number;
}

/** Zähler für die Kopfzeile der Vorschau-Tabelle ("X übernehmbar / Y blockiert / Z ohne Treffer / W fehlgeschlagen"). */
export function countBatchOutcomes(rows: BatchCatalogRow[]): BatchOutcomeCounts {
  const counts: BatchOutcomeCounts = { uebernehmbar: 0, blockiert: 0, keinTreffer: 0, sucheFehlgeschlagen: 0 };
  for (const row of rows) {
    if (row.outcome === 'uebernehmbar') counts.uebernehmbar += 1;
    else if (row.outcome === 'blockiert') counts.blockiert += 1;
    else if (row.outcome === 'suche-fehlgeschlagen') counts.sucheFehlgeschlagen += 1;
    else counts.keinTreffer += 1;
  }
  return counts;
}

/**
 * P2-G: reine Klassifizierung EINES Suchversuchs (HTTP-Antwort ODER Netzwerkfehler) in
 * Kandidaten vs. "Suche fehlgeschlagen". Pure, damit sie ohne echten `fetch()` unter
 * node:test geprüft werden kann — der HTTP-Call selbst bleibt in der UI
 * (client/src/components/kalkulation/catalog-batch-suggest-dialog.tsx::searchMaterialsForPosition).
 * Ein malformtes/fehlendes `materials`-Feld oder ein nicht-boolesches `truncated` bei ok=true
 * ist ein Vertragsfehler und damit ein Suchfehlschlag. Nur `{ materials: [] }` ist ein echter
 * Leertreffer.
 */
export interface MaterialsSearchAttemptResult {
  ok: boolean;
  candidates: CatalogSearchCandidate[];
  /** P1-2: durchgereicht aus der Server-Antwort (siehe server/routes/softec-db.ts) — true, wenn
   * der Server mehr Treffer hatte, als zurückgegeben wurden. Siehe BatchPositionSource.truncated. */
  truncated: boolean;
}

export function classifyMaterialsSearchResponse(
  input: { ok: true; data: unknown } | { ok: false; data: unknown } | { error: true },
): MaterialsSearchAttemptResult {
  if ('error' in input) return { ok: false, candidates: [], truncated: false };
  if (!input.ok) return { ok: false, candidates: [], truncated: false };
  if (
    input.data === null ||
    typeof input.data !== 'object' ||
    Array.isArray(input.data)
  ) {
    return { ok: false, candidates: [], truncated: false };
  }
  const data = input.data as { materials?: unknown; truncated?: unknown };
  const materials = data?.materials;
  if (
    !Array.isArray(materials) ||
    materials.some(
      (material) =>
        material === null ||
        typeof material !== 'object' ||
        Array.isArray(material),
    ) ||
    (
      Object.prototype.hasOwnProperty.call(data, 'truncated') &&
      typeof data.truncated !== 'boolean'
    )
  ) {
    return { ok: false, candidates: [], truncated: false };
  }
  return {
    ok: true,
    candidates: materials as CatalogSearchCandidate[],
    truncated: data?.truncated === true,
  };
}

/**
 * Baut die neue Kalkulation für EINEN Batch-Treffer — exakt derselbe Builder-Pfad wie
 * CatalogSuggestDialog::buildMaterialEkKalkulation und
 * X84PriceImport::buildKalkulationMitMaterialEk (hatKalkulation() MUSS auf dem Stand VOR
 * setzeBlitzZeile geprüft werden, siehe shared/kalkulation.ts::mengenBezugFuerPreisImport).
 * Keine neue Mathematik — nur dieselbe 3-Schritt-Komposition (setzeBlitzZeile →
 * Beschreibung taggen → mengenBezugFuerPreisImport) ein drittes Mal zusammengesetzt.
 */
export function buildMaterialEkKalkulationFuerBatchImport(
  kalkulationBestehend: PositionsKalkulation | null | undefined,
  unitPrice: number,
  quelle: string,
): PositionsKalkulation {
  const hatteVorherKalkulation = hatKalkulation(kalkulationBestehend ?? null);
  const basis = setzeBlitzZeile(kalkulationBestehend ?? null, 'material', { einzelpreis: unitPrice });
  const mitBeschreibung: PositionsKalkulation = {
    ...basis,
    zeilen: basis.zeilen.map((zeile) =>
      zeile.id === BLITZ_ZEILEN_IDS.material ? { ...zeile, beschreibung: `Großhandel: ${quelle}` } : zeile,
    ),
  };
  return mengenBezugFuerPreisImport(mitBeschreibung, hatteVorherKalkulation);
}

/**
 * P1-5: Batch-spezifischer Name für {@link istAktuelleGeneration} (shared/catalog-position-match.ts)
 * — reine Wiederverwendung derselben Generation-Guard-Logik, hier nur unter dem im Fund
 * genannten Namen re-exportiert, damit sie im Batch-Dialog unter ihrem sprechenden Namen
 * verwendet werden kann. Siehe dort für die volle Begründung (Dialog schließen/neu öffnen
 * oder Angebotswechsel während eine Suche läuft ⇒ veraltete Antwort darf neuere Zeilen NICHT
 * überschreiben).
 */
export const sollBatchErgebnisUebernehmen = istAktuelleGeneration;

/**
 * P2-6 (Fix, Codex-Fund): baut den Identitäts-Schlüssel für "welches Angebot ist aktuell
 * geladen" (currentDatabaseId + currentQuoteSlot) — pure, damit der Wechsel-Vergleich unter
 * node:test geprüft werden kann. Siehe {@link hatAngebotIdentitaetGewechselt}.
 */
export function offerIdentityKey(
  databaseId: string | null | undefined,
  quoteSlot: number | null | undefined,
): string {
  return `${databaseId ?? ''}::${quoteSlot ?? ''}`;
}

/**
 * P2-6 (Fund): der Batch-Dialog (catalog-batch-suggest-dialog.tsx) invalidierte bei einem
 * Angebotswechsel (currentDatabaseId/currentQuoteSlot ändert sich WÄHREND der Dialog offen ist)
 * bisher nur die Generation + leerte die Zeilen — er schloss den Dialog aber NICHT und startete
 * auch keine neue Suche. Lief gerade eine Batch-Suche des ALTEN Angebots, erkennt deren eigenes
 * `finally` über die (jetzt veraltete) Generation, dass es NICHT mehr aktuell ist, und überspringt
 * deshalb `setIsSearching(false)` — der Ladespinner bleibt für IMMER hängen (der Dialog zeigt
 * "Suche läuft..." an, obwohl längst ein anderes Angebot geladen ist). Fix: bei einem erkannten
 * Identitätswechsel den Dialog schließen (einfachste sichere Variante, siehe dort) UND
 * `isSearching`/`isApplying` explizit zurücksetzen.
 */
export function hatAngebotIdentitaetGewechselt(vorherigeIdentitaet: string, neueIdentitaet: string): boolean {
  return vorherigeIdentitaet !== neueIdentitaet;
}

export type BatchApplyDisposition =
  | 'vollstaendig-erfolgreich'
  | 'teilweise-erfolgreich'
  | 'vollstaendig-abgelehnt';

export interface BatchApplyOutcome {
  disposition: BatchApplyDisposition;
  appliedPositionIds: number[];
  rejectedPositionIds: number[];
  shouldCloseDialog: boolean;
}

export function classifyBatchApplyOutcome(
  attemptedPositionIds: readonly number[],
  applyResults: readonly boolean[],
): BatchApplyOutcome {
  if (attemptedPositionIds.length !== applyResults.length) {
    throw new Error('attemptedPositionIds und applyResults müssen gleich lang sein');
  }
  const applied = attemptedPositionIds.filter((_, index) => applyResults[index] === true);
  const rejected = attemptedPositionIds.filter((_, index) => applyResults[index] !== true);
  const disposition: BatchApplyDisposition =
    rejected.length === 0
      ? 'vollstaendig-erfolgreich'
      : applied.length > 0
        ? 'teilweise-erfolgreich'
        : 'vollstaendig-abgelehnt';

  return {
    disposition,
    appliedPositionIds: applied,
    rejectedPositionIds: rejected,
    shouldCloseDialog: disposition === 'vollstaendig-erfolgreich',
  };
}

export interface BatchApplyPlanRow {
  positionId: number;
  kalkulation: PositionsKalkulation;
  quelle: string;
  effektiverEk: number;
}

/**
 * Kern-Gate der Bulk-Übernahme: nur Zeilen, die (a) `outcome === 'uebernehmbar'` UND (b) im
 * `checkedPositionIds`-Set stehen, werden übernommen. (a) wird IMMER geprüft — unabhängig vom
 * checked-Set — damit eine blockierte oder treffer-lose Zeile NIE angewendet wird, selbst wenn
 * sie (defensiv/fälschlich) als checked übergeben würde. Kein neuer Persistenz-Weg: der Aufrufer
 * wendet jede zurückgegebene Zeile über den normalen `updatePositionKalkulation`-Store-Pfad an
 * (identisch zu den Einzel-Dialogen).
 */
export function buildBatchApplyPlan(
  rows: BatchCatalogRow[],
  checkedPositionIds: ReadonlySet<number>,
): BatchApplyPlanRow[] {
  const plan: BatchApplyPlanRow[] = [];
  for (const row of rows) {
    if (row.outcome !== 'uebernehmbar') continue;
    if (!checkedPositionIds.has(row.positionId)) continue;
    if (!row.best || row.effektiverEk === null) continue;
    const rawBest = row.best.candidate as CatalogSearchCandidate;
    const quelle = rawBest.description || 'Katalog';
    const kalkulation = buildMaterialEkKalkulationFuerBatchImport(row.kalkulationBestehend, row.effektiverEk, quelle);
    plan.push({ positionId: row.positionId, kalkulation, quelle, effektiverEk: row.effektiverEk });
  }
  return plan;
}

/**
 * P2-3 (Fix, Codex-Review gpt-5.6): der Bulk-Übernahme-Toast (catalog-batch-suggest-dialog.tsx)
 * meldete bisher IMMER die GLOBALE `defaults.materialAufschlagProzent` (z.B. "+15%"), egal was
 * tatsächlich gerechnet wurde. buildMaterialEkKalkulationFuerBatchImport() bewahrt aber einen
 * evtl. vorhandenen Positions- (`kalkulation.materialAufschlagProzent`) oder
 * blitz-material-Zeilen-Override (`zeile.aufschlagProzent`, siehe setzeBlitzZeile) und die
 * Kalkulation (effektiverMaterialAufschlag, shared/kalkulation.ts) bevorzugt ihn VOR dem
 * globalen Default — der Preis war also korrekt, die Toast-Meldung aber irreführend (z.B.
 * real +30% gerechnet, Toast meldet weiterhin "+15%").
 *
 * Fix: reine Ermittlungslogik, welcher Aufschlag je (bereits angewendeter) Kalkulation
 * TATSÄCHLICH effektiv genutzt wurde (derselbe effektiverMaterialAufschlag()-Pfad, den auch
 * die Kalkulation selbst nutzt — keine neue Mathematik) — und ob er über alle Positionen
 * EINHEITLICH ist. Der Aufrufer (Dialog) übergibt NUR die Kalkulationen der tatsächlich
 * angewendeten (nicht der abgelehnten) Positionen, damit die Meldung zum tatsächlichen
 * Ergebnis passt.
 */
export interface BatchAufschlagSummary {
  /** true, wenn ALLE übergebenen Kalkulationen denselben effektiven Aufschlag genutzt haben. */
  einheitlich: boolean;
  /** Der gemeinsame Aufschlag (%), NUR gesetzt wenn `einheitlich` — sonst null. */
  aufschlagProzent: number | null;
}

export function ermittleEffektivenBatchAufschlag(
  kalkulationen: readonly PositionsKalkulation[],
  defaults: KalkulationsDefaults,
): BatchAufschlagSummary {
  if (kalkulationen.length === 0) return { einheitlich: true, aufschlagProzent: null };

  const effektiveWerte = kalkulationen.map((kalkulation) => {
    // Die Blitz-Material-Zeile ist die, die buildMaterialEkKalkulationFuerBatchImport gerade
    // gesetzt hat — fehlt sie (sollte nicht vorkommen, defensiv abgesichert), wird eine leere
    // Zeile angenommen (kein zeilen-eigener Override ⇒ fällt auf Position/Default zurück).
    const zeile =
      kalkulation.zeilen.find((z) => z.id === BLITZ_ZEILEN_IDS.material) ??
      ({ id: BLITZ_ZEILEN_IDS.material, art: 'material' } as const);
    return effektiverMaterialAufschlag(zeile, kalkulation, defaults);
  });

  const ersterWert = effektiveWerte[0]!;
  const einheitlich = effektiveWerte.every((wert) => wert === ersterWert);
  return { einheitlich, aufschlagProzent: einheitlich ? ersterWert : null };
}

/**
 * Ermittelt den Material-Aufschlag für die Einzelvorschau über eine lokale Kalkulationskopie.
 * Der positive Probe-EK bewahrt dabei den blitz-material-Zeilenoverride, ohne etwas zu speichern.
 */
export function ermittleEffektivenMaterialAufschlagFuerVorschau(
  kalkulationBestehend: PositionsKalkulation | null | undefined,
  defaults: KalkulationsDefaults,
): number {
  const probe = buildMaterialEkKalkulationFuerBatchImport(
    kalkulationBestehend,
    1,
    'Vorschau',
  );
  const summary = ermittleEffektivenBatchAufschlag([probe], defaults);
  return summary.aufschlagProzent ?? defaults.materialAufschlagProzent;
}

/**
 * P2-3: formatiert die Toast-Meldung aus {@link ermittleEffektivenBatchAufschlag} — bei
 * einheitlichem Aufschlag die konkrete Zahl (bisheriges Format, jetzt aber tatsächlich
 * zutreffend), bei uneinheitlichen Overrides ein ehrlicher Sammelhinweis statt einer
 * pauschal falschen Prozentzahl. Leere Liste (keine Position tatsächlich angewendet) ⇒ leerer
 * String — `aufschlagProzent === null` bei `einheitlich === true` kommt NUR aus dem leeren
 * Fall (bei uneinheitlichen Overrides ist `einheitlich` immer `false`), ein Aufrufer sollte den
 * leeren String NICHT in Klammern anzeigen (kein "(...)"-Leerlauf in der Meldung).
 */
export function formatBatchAufschlagHinweis(summary: BatchAufschlagSummary): string {
  if (summary.aufschlagProzent !== null) {
    return `+${summary.aufschlagProzent}% Aufschlag`;
  }
  return summary.einheitlich ? '' : 'individuelle Aufschläge je Position';
}
